import { useControls } from "leva";
import { use, useMemo } from "react";
import { GLSL3, Vector2 } from "three";
import {
    STICKER_PAD,
    buildStickerMaps,
    studioEnvTexture,
} from "./holoStickerTextures";
import fragmentShader from "./shaders/holoSticker/fragment.frag";
import vertexShader from "./shaders/holoSticker/vertex.vert";

const DEFAULTS = {
    radius: 0.19,
    holo: 0.75,
    // the disc is a small part of the frame, so the bands run tighter than
    // holosticker's full-screen default to keep the rainbow readable
    bands: 14,
    hue: 0,
    grain: 0.3,
    relief: 0.22,
    seed: 3.08,
    pattern: 0,
    overlay: 1,
};

const maps = new Map();

function useStickerMaps(url) {
    if (!maps.has(url)) maps.set(url, buildStickerMaps(url));
    return use(maps.get(url));
}

export default function HoloSticker({ artwork, ...props }) {
    const { map, height } = useStickerMaps(artwork);
    const sticker = useControls("Sticker", {
        // beyond ~0.23 the disc runs off the flat panel and onto the bevel
        radius: { value: DEFAULTS.radius, min: 0.08, max: 0.23, step: 0.005 },
        holo: { value: DEFAULTS.holo, min: 0, max: 1, step: 0.01 },
        bands: { value: DEFAULTS.bands, min: 1, max: 20, step: 0.5 },
        hue: { value: DEFAULTS.hue, min: 0, max: 1, step: 0.01 },
        grain: { value: DEFAULTS.grain, min: 0, max: 1, step: 0.01 },
        relief: { value: DEFAULTS.relief, min: 0, max: 1, step: 0.01 },
        seed: { value: DEFAULTS.seed, min: 0, max: 8, step: 0.01 },
        pattern: {
            value: DEFAULTS.pattern,
            options: { linear: 0, radial: 1, patches: 2 },
        },
        overlay: {
            value: DEFAULTS.overlay,
            options: { none: 0, triangles: 1, squares: 2, stripes: 3 },
        },
    });

    const uniforms = useMemo(
        () => ({
            uMap: { value: map },
            uHeight: { value: height },
            uEnv: { value: studioEnvTexture() },
            uMaxMip: { value: 8 },
            // the "holo" finish, with the roughness the foil reads at head-on
            uMetal: { value: 0.6 },
            uRough: { value: 0.13 },
            uEnvIntensity: { value: 1 },
            uInk: { value: 1 },
            uLight: { value: new Vector2(0.65, 0.7) },
            uHolo: { value: DEFAULTS.holo },
            uBands: { value: DEFAULTS.bands },
            uHue: { value: DEFAULTS.hue },
            uGrain: { value: DEFAULTS.grain },
            uPattern: { value: DEFAULTS.pattern },
            uOverlay: { value: DEFAULTS.overlay },
            uRelief: { value: DEFAULTS.relief },
            uSeed: { value: DEFAULTS.seed },
        }),
        [map, height],
    );

    // the disc is inset in a padded texture, so the plane is oversized to match
    const span = (2 * sticker.radius) / (1 - 2 * STICKER_PAD);

    return (
        <mesh {...props} raycast={() => null}>
            <planeGeometry args={[span, span]} />
            <shaderMaterial
                glslVersion={GLSL3}
                vertexShader={vertexShader}
                fragmentShader={fragmentShader}
                uniforms={uniforms}
                uniforms-uHolo-value={sticker.holo}
                uniforms-uBands-value={sticker.bands}
                uniforms-uHue-value={sticker.hue}
                uniforms-uGrain-value={sticker.grain}
                uniforms-uPattern-value={sticker.pattern}
                uniforms-uOverlay-value={sticker.overlay}
                uniforms-uRelief-value={sticker.relief}
                uniforms-uSeed-value={sticker.seed}
                transparent
            />
        </mesh>
    );
}
